import { CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';
import { z } from 'zod';

/** What AWS End User Messaging accepts as a destination: `+<country><subscriber>`. */
const E164 = /^\+[1-9]\d{6,14}$/;

/**
 * A phone number in E.164 form.
 *
 * Deliberately only a shape check, not a plausibility one: the numbers AWS hands out for its SMS
 * simulator (`+1 933 …`) are not dialable numbers and would be rejected by any real validation,
 * which would leave the whole opt-in flow untestable outside production. A number that is well
 * formed but goes nowhere fails at the send instead, visibly and per player.
 *
 * Callers normalise with {@link normalizeToE164} before sending rather than relying on a
 * transform here: these payload schemas are encoded on the way out as well, and a one-way
 * `transform` would break that.
 */
export const e164PhoneSchema = z
  .string()
  .trim()
  .regex(E164, 'Phone number must be in E.164 format, e.g. +12025550123');

/**
 * Turns what someone typed into E.164.
 *
 * Uses the phone number library where it can — so a national number typed with the country
 * selected, or an international one with spaces and dashes, both arrive as one canonical string —
 * and falls back to stripping the separators for the numbers it refuses to parse.
 *
 * @param input - The raw input.
 * @param defaultCountry - The country to read a national number as, when given.
 * @returns The number in E.164, or an empty string when the input cannot be made into one.
 */
export function normalizeToE164(input: string, defaultCountry?: CountryCode): string {
  const trimmed = input.trim();

  if (!trimmed) {
    return '';
  }

  try {
    return parsePhoneNumberWithError(trimmed, defaultCountry).format('E.164');
  } catch {
    const stripped = trimmed.replace(/[\s()./-]/g, '');

    return E164.test(stripped) ? stripped : '';
  }
}

export type SmsOptInPayload = z.output<typeof smsOptInPayloadSchema>;
export type SmsOptInPayloadDTO = z.input<typeof smsOptInPayloadSchema>;

/**
 * What a player user sends to turn text message notifications on.
 *
 * Both consent flags are `true` literals rather than booleans: an opt-in without them is not a
 * weaker opt-in, it is no opt-in at all, and the record we keep of it is only worth keeping if
 * the request that created it could not have carried anything else.
 */
export const smsOptInPayloadSchema = z.object({
  /** Consent to receive recurring automated messages at {@link SmsOptInPayload.phone}. */
  consentMessages: z.literal(true),
  /** Acceptance of the terms of service and privacy policy. */
  consentTerms: z.literal(true),
  /** The mobile number the messages are sent to. */
  phone: e164PhoneSchema,
});
